import DimensionUtils from "../common/DimensionUtils";
import { resolveUnitConfig, unitNamesOf } from "../common/inputs/unitConfig";
import IUnitsTypeDescriptor from "../database/interfaces/paramScript/IUnitsTypeDescriptor";
import IParamTypeDescriptor, {
    IBackPassingParamTypeDescriptor,
    IBackPassingSizeField,
    IBoolParamTypeDescriptor,
    ICountParamTypeDescriptor,
    IEnumParamTypeDescriptor,
    IRailingParamTypeDescriptor,
    IRelationFieldOverride,
    ISizeParamTypeDescriptor,
} from "../database/interfaces/paramScript/IParamTypeDescriptor";
import {
    isItemVisible,
    itemOverride,
    itemsOf,
    resolveItems,
    withProportionalTail,
    TItemsModel,
} from "./paramItems";

/**
 * BETÖLTÉSI GUARD — az ELTÁROLT paraméter-értékek öngyógyítása a SAJÁT descriptorukhoz, a
 * configurátor első felépítésénél (ParametersProcessor build-effekt).
 *
 * MIÉRT KELL:
 * A mentett értékek (uc_style_parameter_values — a globális/shop-szintű defaultok ÉS a
 * customer/cart-scope-ú felhasználói preferenciák egyaránt) TÚLÉLIK a descriptor változását. A
 * descriptor korlátait (min/max, egység-lista, enum-opciók) viszont csak a KÁRTYÁK érvényesítik, a
 * felhasználó szerkesztése közben (NumericInput clamp, EnumSelectorInput opciólista). Ha egy korlátot
 * UTÓLAG szigorítunk az adatban (pl. V54: egyenes munkalap hossza min 1000, korábban 600 is mehetett),
 * akkor a régen mentett 600mm változatlanul betöltődik, kimegy a Unity-nak, és így kerül a kosárba is —
 * pedig ma már érvénytelen. A UI-ból nem is mindig javítható (rejtett/zárolt mező), és a felhasználó
 * nem is tudja, hogy javítania kell.
 *
 * MIT TESZ: a betöltéskor kapott értéket a descriptor szerint ÉRVÉNYES legközelebbi értékre hozza —
 * pontosan UGYANAZOKKAL a korlátokkal, amiket a kártya is érvényesítene, ha a felhasználó most nyúlna a
 * mezőhöz (ezért használja a kártyákkal MEGOSZTOTT resolveUnitConfig-ot: egy forrásból jön a min/max,
 * az egység és a tizedesek). Így a látszó mező, a Unity-kimenet és a kosár-descriptor SOSE mondhat mást,
 * mint amit a descriptor engedélyez.
 *
 * SZABÁLYOK (a meglévő viselkedés sértetlenségéhez):
 *   1. CSAK a descriptorban DEKLARÁLT korlát léphet közbe. Ahol nincs korlát, ott nincs beavatkozás
 *      (a unitConfig strukturális fallbackjai — min 0, max MAX_SAFE_INTEGER — a gyakorlatban sosem
 *      harapnak). Kódba írt üzleti érték NINCS.
 *   2. MEGLÉVŐ mezőket javítunk, ÚJAT nem veszünk fel: egy composite (BackPassing/Railing) értékbe a
 *      guard nem tesz be olyan kulcsot, ami az eltárolt objektumban nem szerepelt — különben a Unity
 *      felé menő JSON alakja változna. A hiányzó mezők defaultolása változatlanul a kártyák dolga.
 *   3. ÜRES érték nem "stale érték", hanem "nincs érték": az alapértéket szándékosan a descriptor
 *      (illetve a kártya) adja, ezért üres értéknél a guard nem figyelmeztet.
 *   4. Composite értéknél csak azt a mezőt guardoljuk, amelyik az AKTUÁLIS állapotban LÁTSZANA is
 *      (BackPassing `visibleForRelations`, Railing `visibleForTypes`) — ugyanaz a feltétel, mint a
 *      kártyán. Amit a felhasználó most nem is szerkeszthet, ahhoz nem nyúlunk.
 *   5. A DB-t NEM írjuk vissza: a guard csak azt hozza rendbe, ami a state-be (és onnan a Unity-ba /
 *      kosárba) kerül. A felhasználó mentett sora a következő tényleges változásig érintetlen marad
 *      (akkor a notifyParamChanged amúgy is a guardolt értéket menti).
 *
 * ÚJ TÍPUS BEKÖTÉSE: egy sor a PARAM_GUARDS regiszterbe (a Decor szándékosan NEM itt van: annak saját,
 * adat-létezésre épülő fallbackja van a ParametersProcessorban — lásd decorFallback).
 */

/** Egy guard-beavatkozás nyoma: mit talált, mire javította és miért (a build-effekt logolja). */
export type TGuardNote = {
    /** Composite értéknél a javított mező kulcsa (pl. "RailingWidth"); egyszerű értéknél nincs. */
    field?: string;
    /** Az eltárolt (bejövő) érték. */
    from: string;
    /** Az érvényes érték, amire javítottuk. */
    to: string;
    /**
     * `unit`   — az egység nem szerepel a descriptor `units`-ában (vagy a szám nem parse-olható),
     * `range`  — a szám a descriptor min/max korlátain kívül esett,
     * `option` — az érték nem létező enum-opció.
     */
    reason: "unit" | "range" | "option";
};

export type TGuardResult = { value: any; notes: TGuardNote[] };

/** Beavatkozás nélküli eredmény (a leggyakoribb eset: az eltárolt érték érvényes). */
const unchanged = (value: any): TGuardResult => ({ value, notes: [] });

/** Nem definiált korlát sosem harap: `undefined` bound esetén a szám marad önmaga. */
const clamp = (num: number, min?: number, max?: number): number =>
    Math.min(Math.max(num, min ?? num), max ?? num);

/**
 * Egy méret-mező ("600mm" alakú string) guardolása a `units` descriptorához. A számot és az egységet a
 * kártyákkal MEGOSZTOTT resolveUnitConfig oldja fel (stale egység / nem parse-olható szám → a descriptor
 * első egységének defaultja), a számot pedig a mező korlátai közé szorítjuk. Az `override` a kötés-függő
 * felülírás (BackPassing `byRelation[kötés]`), ugyanúgy, ahogy a kártya is adja a NumericInputnak.
 */
export const guardDimension = (
    units: IUnitsTypeDescriptor | undefined,
    value: any,
    override?: IRelationFieldOverride,
    field?: string
): TGuardResult => {
    // Descriptor/units nélkül nincs mihez validálni — az érték változatlan.
    if (!unitNamesOf(units).length) return unchanged(value);

    const raw = String(value ?? "");
    const parsed = DimensionUtils.parseDimension(raw);
    const cfg = resolveUnitConfig(units, raw);
    const num = clamp(cfg.num, override?.min ?? cfg.min, override?.max ?? cfg.max);

    const reason: TGuardNote["reason"] | undefined =
        isNaN(parsed.num) || !unitNamesOf(units).includes(parsed.unit)
            ? "unit"
            : num !== parsed.num
                ? "range"
                : undefined;
    if (!reason) return unchanged(value);

    const to = DimensionUtils.formatDimension(num, cfg.unit);
    // Üres értéknél a descriptor adja az alapértéket — ez nem adat-eltérés, nem figyelmeztetünk.
    return { value: to, notes: raw === "" ? [] : [{ field, from: raw, to, reason }] };
};

/**
 * Enum-szerű érték guardolása egy opció-listához. Az `extraValid` azokat az érvényes értékeket sorolja
 * fel, amelyek nem választható opciók, de a descriptor ismeri őket (pl. a railing `offValue`/`onValue`).
 */
const guardOption = (
    value: any,
    options: { [name: string]: string } | undefined,
    fallback: string | undefined,
    extraValid: (string | undefined)[] = [],
    field?: string
): TGuardResult => {
    const keys = Object.keys(options ?? {});
    if (!keys.length) return unchanged(value);

    const raw = String(value ?? "");
    // Üres érték: a kártya ilyenkor a descriptor defaultját mutatja — ez nem stale érték.
    if (raw === "" || keys.includes(raw) || extraValid.includes(raw)) return unchanged(value);

    const to = fallback ?? keys[0];
    return { value: to, notes: [{ field, from: raw, to, reason: "option" }] };
};

/**
 * Composite (objektum) értékek guardolása: mezőnként alkalmazza a megadott guardot, és CSAK a MÁR
 * MEGLÉVŐ kulcsokhoz nyúl (lásd 2. szabály). Ha egyik mező sem változott, az EREDETI objektumot adja
 * vissza (nincs felesleges új referencia).
 */
const guardFields = (
    value: any,
    fields: Array<{ key: string; guard: (fieldValue: any, key: string) => TGuardResult }>
): TGuardResult => {
    if (!value || typeof value !== "object") return unchanged(value);

    const out: Record<string, any> = { ...value };
    const notes: TGuardNote[] = [];
    let changed = false;

    for (const { key, guard } of fields) {
        if (!(key in out)) continue;
        const result = guard(out[key], key);
        if (!Object.is(result.value, out[key])) {
            out[key] = result.value;
            changed = true;
        }
        notes.push(...result.notes);
    }

    return { value: changed ? out : value, notes };
};

/** Size / Size1D: egyetlen méret-mező a param `units` descriptorával. */
const guardSize = (value: any, d: ISizeParamTypeDescriptor): TGuardResult =>
    guardDimension(d?.units, value);

/**
 * Count: egész szám a `config` min/max korlátai között. Nem szám (üres/hibás) értéknél nem avatkozunk
 * be — azt a kártya kezeli; a guard csak a korlátokon kívüli, egyébként értelmes számot javítja.
 */
const guardCount = (value: any, d: ICountParamTypeDescriptor): TGuardResult => {
    const cfg = d?.config;
    if (!cfg || (cfg.min === undefined && cfg.max === undefined)) return unchanged(value);

    const raw = String(value ?? "");
    const num = parseInt(raw, 10);
    if (isNaN(num)) return unchanged(value);

    const guarded = clamp(num, cfg.min, cfg.max);
    if (guarded === num) return unchanged(value);

    const to = String(guarded);
    return { value: to, notes: [{ from: raw, to, reason: "range" }] };
};

/** Enum: az érték csak létező opció lehet; stale értéknél a descriptor defaultja (végül az első opció). */
const guardEnum = (value: any, d: IEnumParamTypeDescriptor): TGuardResult =>
    guardOption(value, d?.options, d?.default as string | undefined);

/**
 * BackPassing {Relation, Depth, Margin, Top}: a kötés csak létező opció lehet, a méret-mezők pedig a
 * KÖTÉS-FÜGGŐ korlátok (`byRelation[kötés]`) közé esnek — ugyanaz a feloldás, mint a kártyán. Azt a
 * méret-mezőt, amelyik az adott kötésnél nem is látszik (`visibleForRelations`), nem bántjuk.
 */
const guardBackPassing = (value: any, d: IBackPassingParamTypeDescriptor): TGuardResult => {
    if (!value || typeof value !== "object") return unchanged(value);

    const relationOptions = Object.keys(d?.relation?.options ?? {});
    // A méret-mezők korlátait az ÉRVÉNYES kötés választja ki: ha az eltárolt kötés maga is stale, akkor
    // a guardolt kötés (default / első opció) szerinti korlátokkal dolgozunk — nem a hibás értékkel.
    const relationResult = guardOption(
        value.Relation, d?.relation?.options, d?.relation?.default, [], "Relation"
    );
    const relation = String(
        relationResult.value ?? d?.relation?.default ?? relationOptions[0] ?? ""
    );

    const sizeField = (field: IBackPassingSizeField | undefined) =>
        (fieldValue: any, key: string): TGuardResult =>
            (field?.visibleForRelations?.includes(relation) ?? true)
                ? guardDimension(field?.units, fieldValue, field?.byRelation?.[relation], key)
                : unchanged(fieldValue);

    return guardFields(value, [
        { key: "Relation", guard: () => relationResult },
        { key: "Depth", guard: sizeField(d?.depth) },
        { key: "Margin", guard: sizeField(d?.margin) },
        // A látható oldal is csak létező opció lehet — de csak ott javítjuk, ahol az adott kötésnél
        // látszik is, hogy egy kötés-váltás ne mossa el a user választását (mint a méret-mezőknél).
        {
            key: "VisibleSide",
            guard: (fieldValue, key) =>
                (d?.visibleSide?.visibleForRelations?.includes(relation) ?? true)
                    ? guardOption(fieldValue, d?.visibleSide?.options, d?.visibleSide?.default, [], key)
                    : unchanged(fieldValue),
        },
    ]);
};

/**
 * Railing {RailingType, RailingWidth}: a típus csak létező opció (vagy a descriptor `offValue`/
 * `onValue`-ja) lehet, a szélesség pedig a `railingWidth.units` korlátai közé esik — de csak akkor, ha
 * a szélesség az adott típusnál látszik is (`visibleForTypes`), ahogy a kártyán.
 */
const guardRailing = (value: any, d: IRailingParamTypeDescriptor): TGuardResult => {
    if (!value || typeof value !== "object") return unchanged(value);

    const typeResult = guardOption(
        value.RailingType,
        d?.railingType?.options,
        d?.railingType?.default ?? d?.railingType?.offValue,
        [d?.railingType?.offValue, d?.railingType?.onValue],
        "RailingType"
    );
    // A szélesség láthatóságát ugyanaz a fallback-lánc dönti el, mint a kártyán (érték → default →
    // offValue), hogy a guard és a UI ugyanarra a típusra nézze a `visibleForTypes`-t.
    const railingType = String(
        typeResult.value ?? d?.railingType?.default ?? d?.railingType?.offValue ?? ""
    );
    const showWidth = d?.railingWidth?.visibleForTypes?.includes(railingType) ?? true;

    return guardFields(value, [
        { key: "RailingType", guard: () => typeResult },
        {
            key: "RailingWidth",
            guard: (fieldValue, key) => showWidth
                ? guardDimension(d?.railingWidth?.units, fieldValue, undefined, key)
                : unchanged(fieldValue),
        },
    ]);
};

/**
 * Bool: a TÁROLÁSI alak string ("true"/"false" — a `style_parameter_value` varchar), a Unity felé
 * viszont IGAZI JSON boolean megy. Eddig ezt a konverziót KIZÁRÓLAG a kártya végezte el, tehát az
 * ELSŐ betöltés a stringet küldte ki (`{"DoubleFront":"false"}`), és a boolean csak az első kattintás
 * után jelent meg. A tárolási → futásidejű alak normalizálásának a helye pontosan ez a réteg: itt
 * egyszer megtörténik, minden fogyasztó (kártya, kosár-descriptor, stylescript) előtt.
 *
 * Bármi más (üres, szemét, már boolean) a descriptor deklarált `default`-jára esik. Figyelmeztetés
 * CSAK értelmezhetetlen értéknél: a "true"/"false" string nem adat-eltérés, csak a tárolási alak
 * (3. szabály: üres érték sem az).
 */
const guardBool = (value: any, d: IBoolParamTypeDescriptor): TGuardResult => {
    const raw = String(value ?? "");
    const to = (raw === "true" || raw === "false") ? raw === "true" : (d?.default ?? false);
    if (Object.is(value, to)) return unchanged(value);

    const known = raw === "" || raw === "true" || raw === "false";
    return { value: to, notes: known ? [] : [{ from: raw, to: String(to), reason: "option" }] };
};

/**
 * TÖBB AL-MEZŐS érték guardolása — mindkét forrásra (`fields` és `count`), a MEGOSZTOTT modellel.
 *
 * `fields`: mezőnként a levél-fajtájának megfelelő guard fut. A 4. szabályt tartjuk — csak azt a mezőt
 * guardoljuk, amelyik az AKTUÁLIS állapotban LÁTSZANA is (a kártyával közös `isItemVisible` dönt) —,
 * és a 2. szabályt is (a `guardFields` csak a MÁR MEGLÉVŐ kulcsokhoz nyúl).
 *
 * `count`: elemenként a lista `units`-ához guardolt méret, majd a LEGALÁBB EGY arányos elem szabályának
 * kikényszerítése. Az ELEMSZÁMHOZ nem nyúlunk: azt a darabszám-paraméter adja, amit a guard nem lát.
 */
const guardItems = (value: any, model: TItemsModel): TGuardResult => {
    if (!value || typeof value !== "object") return unchanged(value);

    if (model.source === "fields") {
        const fields = itemsOf(model);

        return guardFields(value, fields.map((field) => ({
            key: field.key,
            guard: (fieldValue: any, key: string): TGuardResult => {
                if (!isItemVisible(field, fields, value)) return unchanged(fieldValue);

                if (field.kind === "size") {
                    return guardDimension(field.units, fieldValue, itemOverride(field, fields, value), key);
                }
                if (field.kind === "enum") return guardOption(fieldValue, field.options, field.default, [], key);

                // Egész szám: a `config` min/max korlátai közé szorítva. Nem szám (üres/hibás) értéknél
                // nem avatkozunk be — azt a kártya kezeli; ugyanaz a szabály, mint a `guardCount`-nál.
                if (field.kind === "number") {
                    const cfg = field.config;
                    const rawNum = String(fieldValue ?? "");
                    const num = parseInt(rawNum, 10);
                    if (isNaN(num) || (cfg?.min === undefined && cfg?.max === undefined)) {
                        return unchanged(fieldValue);
                    }
                    const clamped = clamp(num, cfg.min, cfg.max);
                    if (clamped === num) return unchanged(fieldValue);

                    const target = String(clamped);
                    return { value: target, notes: [{ field: key, from: rawNum, to: target, reason: "range" }] };
                }

                // Logikai: a tárolási alak "true"/"false" string. Bármi más (üres, szemét, igazi boolean)
                // a mező deklarált alapértékére áll — így a Unity mindig értelmezhető értéket kap.
                const raw = String(fieldValue ?? "");
                if (raw === "true" || raw === "false") return unchanged(fieldValue);

                const to = String(field.default ?? false);
                return { value: to, notes: raw === "" ? [] : [{ field: key, from: raw, to, reason: "option" }] };
            },
        })));
    }

    const stored = value[model.listKey];
    if (!Array.isArray(stored)) return unchanged(value);

    const units = model.item.kind === "size" ? model.item.units : undefined;
    const notes: TGuardNote[] = [];
    const guarded = stored.map((item: any, index: number) => {
        const result = guardDimension(units, item, undefined, `${model.listKey}[${index}]`);
        notes.push(...result.notes);
        return String(result.value);
    });

    const list = withProportionalTail(model, guarded);
    const changed = list.some((item, index) => item !== String(stored[index]));

    return changed ? { value: { ...value, [model.listKey]: list }, notes } : { value, notes };
};

/**
 * Paraméter-típus → guard. A Decor NEM szerepel itt: annak a reziliencia-logikája (eltűnt lap/él →
 * első érvényes, él-mezők defaultjai) adat-LÉTEZÉSRE épül, nem descriptor-korlátra, és a
 * ParametersProcessorban él (decorFallback). Új típus bekötése egy sor.
 */
const PARAM_GUARDS: Record<string, (value: any, descriptor: any) => TGuardResult> = {
    Size: guardSize,
    Count: guardCount,
    Enum: guardEnum,
    Bool: guardBool,
    BackPassing: guardBackPassing,
    Railing: guardRailing,
};

/**
 * Egy betöltött paraméter-érték guardolása a típusa + descriptora szerint. Ismeretlen típusnál vagy
 * descriptor nélkül nincs beavatkozás. (A "Size1D" alias normalizálása a hívó dolga — a build-effekt
 * amúgy is "Size"-ra írja át, mielőtt ide ér.)
 */
export const guardParameterValue = (
    parameterType: string | null,
    value: any,
    descriptor?: IParamTypeDescriptor
): TGuardResult => {
    if (!descriptor) return unchanged(value);

    // ÜRES TÁROLT ÉRTÉK → a descriptor deklarált alapértéke. A Size-ág ezt eddig is megtette
    // (guardDimension üres bemenetre a `units.<egység>.default`-ot adja), az Enum/Count viszont üresen
    // hagyta — így az a paraméter, amelyiknek nincs érték-sora, `null`-ként ment volna ki a Unitynak.
    // Ez teszi használhatóvá a TERMÉKENKÉNTI alapértéket: a `uc_style_parameter_values` nem
    // termék-szintű, tehát egy terméknek csak úgy lehet saját alapértéke, ha a globális sor NINCS, és a
    // descriptor dönt — pontosan az a rangsor, amit a paramDefaults fejléce leír.
    // Csak a SKALÁR defaultot deklaráló descriptorokra hat; a composite-ok (Decor, Hinges, BackPassing)
    // gyökerén nincs `default`, azok változatlanul a saját guardjukon mennek át.
    const scalarDefaults = descriptor as { default?: unknown; config?: { default?: unknown } };
    const declared = scalarDefaults.default ?? scalarDefaults.config?.default;
    if ((value === null || value === undefined || value === "") && declared !== undefined) {
        return unchanged(typeof declared === "boolean" ? declared : String(declared));
    }

    // A NEVESÍTETT guard mindig erősebb: a Railing/BackPassing mezői egymásra hatnak, azt a generikus
    // ág nem tudná lekövetni. Csak ha nincs ilyen, nézünk a descriptor ALAKJÁRA — így egy új
    // objektum-értékű típus (CabinetTilt, ToeKick, …) guardot is kap, regiszter-sor nélkül.
    const guard = parameterType ? PARAM_GUARDS[parameterType] : undefined;
    if (guard) return guard(value, descriptor);

    // A forrás felismerése (és a két ág SORRENDJE) a megosztott `resolveItems`-ben van.
    const model = resolveItems(descriptor);
    return model ? guardItems(value, model) : unchanged(value);
};

/** Egy guard-beavatkozás emberi olvasásra (konzol-figyelmeztetés a build-effektben). */
export const describeGuardNote = (paramName: string, note: TGuardNote): string => {
    const what = note.reason === "unit"
        ? "does not match its descriptor units"
        : note.reason === "range"
            ? "is outside its descriptor limits"
            : "is not a valid descriptor option";
    const target = note.field ? `${paramName}.${note.field}` : paramName;
    return `[paramGuards] Stored value '${note.from}' of '${target}' ${what}; loaded as '${note.to}' instead.`;
};

export default guardParameterValue;
